import { Fragment, useState } from 'react';
import { Tooltip } from '@odo/shared-ui';
import { useIsTextTruncated } from '../../../hooks/useIsTextTruncated';
import { RingGauge } from './RingGauge';
import { STAT_RING_MAX_SIZE, statRingStrokeWidth } from './statRingSize';
import { statRingSlotClass, useStatRingFitSize } from './StatRingSizeContext';
import { STAT_RING_SUBLABEL_SEPARATOR, splitStatRingSublabel } from './statRingSublabel';

// Narrow fitted slots wrap labels onto a second line; only a word wider than the slot gets an ellipsis.
const FITTED_TEXT_CLASS = 'line-clamp-2 break-words';
const SUBLABEL_CLASS = 'text-xs text-gray-500 max-w-full';

interface StatRingProps {
    pct: number | null;
    color: string;
    label: string;
    sublabel: string;
    title?: string;
    emptyLabel?: string;
}

function SublabelWord({ text }: { text: string }) {
    const [element, setElement] = useState<HTMLSpanElement | null>(null);
    const truncated = useIsTextTruncated(element, text);
    return (
        <span
            ref={setElement}
            title={truncated ? text : undefined}
            className="inline-block max-w-full truncate align-top"
        >
            {text}
        </span>
    );
}

// Moves to the next line as a unit; only a part wider than the whole slot breaks, at its spaces.
function SublabelPart({ text }: { text: string }) {
    return (
        <span className="inline-block max-w-full align-top">
            {text
                .split(' ')
                .filter((word) => word.length > 0)
                .map((word, index) => (
                    <Fragment key={`${index}-${word}`}>
                        {index > 0 && ' '}
                        <SublabelWord text={word} />
                    </Fragment>
                ))}
        </span>
    );
}

// A narrow slot wraps between "0 go" and "4 no-go/gb", never inside a hyphenated token.
function FittedSublabel({ sublabel }: { sublabel: string }) {
    return (
        <span className={SUBLABEL_CLASS}>
            {splitStatRingSublabel(sublabel).map((part, index) => (
                <Fragment key={`${index}-${part}`}>
                    {index > 0 && STAT_RING_SUBLABEL_SEPARATOR}
                    <SublabelPart text={part} />
                </Fragment>
            ))}
        </span>
    );
}

function StatRing({ pct, color, label, sublabel, title, emptyLabel = 'No data' }: StatRingProps) {
    const hasValue = pct != null;
    const fitSize = useStatRingFitSize();
    const fitted = fitSize != null;
    const size = fitSize ?? STAT_RING_MAX_SIZE;

    const body = (
        <div className={statRingSlotClass(fitted)}>
            <RingGauge
                pct={pct}
                color={color}
                size={size}
                strokeWidth={statRingStrokeWidth(size)}
                valueClassName="text-sm font-semibold"
            />
            <div className="flex flex-col items-center text-center min-w-0 max-w-full">
                <span
                    className={`text-sm font-medium text-gray-800 max-w-full ${fitted ? FITTED_TEXT_CLASS : 'truncate'}`}
                >
                    {hasValue ? label : emptyLabel}
                </span>
                {fitted ? (
                    <FittedSublabel sublabel={sublabel} />
                ) : (
                    <span className={`${SUBLABEL_CLASS} truncate`}>{sublabel}</span>
                )}
            </div>
        </div>
    );

    return title ? <Tooltip content={title}>{body}</Tooltip> : body;
}

export { StatRing };
